import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that displays a marquee.
 * Uses CSS for animation, JS only for calculating clones and speed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /** @type {MediaQueryList | null} */
  #breakpointMql = null;

  #onBreakpointChange = () => {
    this.#handleResize();
  };

  async connectedCallback() {
    super.connectedCallback();

    this.#breakpointMql = window.matchMedia('(max-width: 1023px)');
    this.#breakpointMql.addEventListener('change', this.#onBreakpointChange);

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const numberOfCopies = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    
    // Use CSS variable for pause/resume instead of Web Animations API
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#breakpointMql?.removeEventListener('change', this.#onBreakpointChange);
    this.#breakpointMql = null;
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
  }

  #marqueeWidth = null;

  #pause = () => {
    this.style.setProperty('--marquee-status', 'paused');
  };

  #resume = () => {
    this.style.setProperty('--marquee-status', 'running');
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve(1), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve(marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth));
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * Duration multiplier: higher = slower scroll. Default 25 matches previous hardcoded data-speed-factor.
   * Desktop vs mobile: attributes data-speed-factor-desktop / data-speed-factor-mobile; legacy data-speed-factor still supported.
   */
  #getSpeedFactor() {
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    const desktop = this.getAttribute('data-speed-factor-desktop');
    const mobile = this.getAttribute('data-speed-factor-mobile');
    const legacy = this.getAttribute('data-speed-factor');

    if (isMobile && mobile != null && mobile !== '') {
      const n = Number(mobile);
      if (!Number.isNaN(n)) return n;
    }
    if (!isMobile && desktop != null && desktop !== '') {
      const n = Number(desktop);
      if (!Number.isNaN(n)) return n;
    }
    if (legacy != null && legacy !== '') {
      const n = Number(legacy);
      if (!Number.isNaN(n)) return n;
    }
    return 25;
  }

  #calculateSpeed(numberOfCopies) {
    const speedFactor = this.#getSpeedFactor();
    return Math.sqrt(numberOfCopies) * speedFactor;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const newNumberOfCopies = await this.#queryNumberOfCopies();
    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
  }, 250);

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);
    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
